import { useState, type MouseEvent } from 'react';

import { Check, Copy } from 'lucide-react';

import { cn } from '@/nucleo/utilitarios';

interface CopyableTextProps {
  /** Texto copiado, cru (CPF, placa, protocolo). Sem valor, so o texto aparece — sem botao de copia. */
  value: string | null | undefined;
  /**
   * Texto exibido, se diferente do copiado — ex.: CPF mascarado enquanto o valor cru vai para a area de transferencia.
   * Omitido (`undefined`), mostra o proprio `value`. String vazia (`''`) e respeitada: nao mostra texto
   * nenhum, mas o botao de copiar continua la, copiando o `value` cru.
   */
  display?: string;
  /** Rotulo para leitor de tela, ex.: "Copiar CPF". */
  label?: string;
  /** Classe de largura maxima (ex.: "max-w-[200px]"), para texto que precisa cortar em vez de estourar a coluna. */
  maxWidth?: string;
  className?: string;
}

/**
 * Mostra um valor e permite copia-lo — util para CPF, placa e protocolo, que
 * as pessoas precisam colar em outro sistema (docs/DESIGN-SYSTEM.md, secao 5).
 *
 * O botao de copiar e alcancavel por teclado e tem rotulo proprio; o retorno
 * visual (o "copiado") e anunciado por aria-live, para quem nao ve a troca de
 * icone.
 *
 * O clique no botao nao sobe para o elemento pai: em listagem com detalhe a
 * linha inteira e clicavel (regra 9 de interface), e copiar um CPF ou um
 * protocolo nao pode abrir o detalhe.
 */
export function CopyableText({ value, display, label, maxWidth, className }: CopyableTextProps) {
  const [copiado, setCopiado] = useState(false);
  const texto = display ?? value ?? '';

  const copiar = async (evento: MouseEvent<HTMLButtonElement>) => {
    evento.stopPropagation();
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 2000);
    } catch {
      // Sem area de transferencia disponivel: nao ha o que fazer alem de nao
      // quebrar. O valor continua visivel para copia manual.
    }
  };

  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      <span className={cn('font-mono text-sm tabular-nums', maxWidth && 'truncate', maxWidth)}>
        {texto}
      </span>
      {value && (
        <button
          type="button"
          onClick={copiar}
          aria-label={label ?? `Copiar ${value}`}
          className="text-muted-foreground hover:text-foreground focus-visible:ring-ring inline-flex h-6 w-6 items-center justify-center rounded transition-colors focus:outline-hidden focus-visible:ring-2"
        >
          {copiado ? (
            <Check className="text-success h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <Copy className="h-3.5 w-3.5" aria-hidden="true" />
          )}
        </button>
      )}
      <span className="sr-only" aria-live="polite">
        {copiado ? 'Copiado' : ''}
      </span>
    </span>
  );
}
